import React, { useEffect, useState, useCallback, useContext, useMemo } from "react";
import api, { BASE_URL } from "../../Utils/api";
import {
  Card,
  Collapse,
  Badge,
  Spinner,
  Table,
  Form,
  Button,
  Row,
  Col,
  InputGroup,
  Modal,
  ListGroup,
} from "react-bootstrap";
import {
  FaFilter, FaSearch, FaTimes, FaSave, FaDownload, FaFilePdf,
  FaPlus, FaExpandAlt, FaCompressAlt, FaCog,
} from "react-icons/fa";
import PageHeader from "../PageHeader/PageHeader";
import formatarData from "../../Utils/formatarData";
import calcDiasParaVcto from "../../Utils/calcDiasParaVcto";
import { GlobalContext } from "../../Utils/GlobalContext";
import { applyDateFilter, getFilterableValue } from "../../Utils/filtrosGrid";
import DocumentosModal from "../Documentos/DocumentosModal";
import gerarDocumentoPdf from "../../Utils/gerarDocumentoPdf";
import PainelConsolidadoConfigModal, {
  CAMPOS_AGRUPAMENTO,
  AGRUPAMENTO_PADRAO,
} from "./PainelConsolidadoConfigModal";
import "./PainelConsolidadoDocumentos.css";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import { podeAdicionarDocumentoAtivo } from "../../Utils/verificarLimiteDocumentosAtivos";

const FILTER_COLUMNS = [
  { Header: "Empresa",           accessor: "fantasia",      getFilterValue: (row) => row.empresa || row.fantasia },
  { Header: "Fornecedor",        accessor: "fornecedor" },
  { Header: "Tipo Documento",    accessor: "tipo_documento" },
  { Header: "Número/Referência", accessor: "nr_contrato" },
  { Header: "Vencimento",        accessor: "dt_vencimento", type: "date" },
  { Header: "Situação",          accessor: "situacao" },
];

const TIPO_DASHBOARD = "painel_consolidado";
const TIMEOUT_CARREGAMENTO_MS = 30000;

function getGroupLabel(campoKey, valor) {
  if (campoKey === "situacao") {
    return valor === "V" ? "Vigente" : valor === "A" ? "Arquivado" : valor === "I" ? "Inativo" : valor || "—";
  }
  return valor || "—";
}

function buildTreeDynamic(docs, nivelKeys) {
  const niveis = nivelKeys
    .map(k => CAMPOS_AGRUPAMENTO.find(c => c.key === k))
    .filter(Boolean);

  if (niveis.length === 0) {
    return [{
      key: "__todos__", label: "Todos os documentos",
      campo: null, campoLabel: null, profundidade: 0,
      docs, children: [], total: docs.length,
    }];
  }

  function agrupar(items, levelIdx) {
    const campo = niveis[levelIdx];
    const map = {};
    const order = [];
    for (const doc of items) {
      const rawVal = doc[campo.key];
      const label = getGroupLabel(campo.key, rawVal != null ? String(rawVal) : null);
      if (!map[label]) { map[label] = []; order.push(label); }
      map[label].push(doc);
    }
    const isLast = levelIdx === niveis.length - 1;
    return order.sort((a, b) => a.localeCompare(b, "pt-BR")).map((label) => {
      const groupDocs = map[label];
      return {
        key: `${campo.key}__${label}__${levelIdx}`,
        label,
        campo: campo.key,
        campoLabel: campo.label,
        profundidade: levelIdx,
        docs: isLast ? groupDocs : [],
        children: isLast ? [] : agrupar(groupDocs, levelIdx + 1),
        total: groupDocs.length,
      };
    });
  }

  return agrupar(docs, 0);
}

function coletarChaves(nodes) {
  const keys = {};
  function walk(list) {
    for (const n of list) {
      keys[n.key] = true;
      if (n.children?.length) walk(n.children);
    }
  }
  walk(nodes);
  return keys;
}

// Paleta visual por profundidade
const PALETA = [
  { border: "#0d6efd", bg: "#e7f1ff", header: "#cfe2ff", cor: "#084298", icone: "fa-building" },
  { border: "#198754", bg: "#f0f9f4", header: "#d1e7dd", cor: "#0f5132", icone: "fa-tags" },
  { border: "#fd7e14", bg: "#fff4e6", header: "#ffe5d0", cor: "#a63d00", icone: "fa-file-alt" },
  { border: "#6f42c1", bg: "#f3f0ff", header: "#e0d7f8", cor: "#3d1a78", icone: "fa-folder" },
  { border: "#0dcaf0", bg: "#e3f8fc", header: "#cff4fc", cor: "#055160", icone: "fa-layer-group" },
];

function TreeNode({ node, totalNiveis, expandedNodes, toggleNode, handleEdit, handleAddNew, handlePrintPdf, getDiasBadgeVariant }) {
  const isOpen = expandedNodes[node.key] === true;
  const profundidade = node.profundidade;
  const isRoot = profundidade === 0;
  const isLeaf = node.docs.length > 0 || totalNiveis === 0;

  const paleta = PALETA[profundidade % PALETA.length];

  const headerStyle = {
    backgroundColor: paleta.header,
    color: paleta.cor,
    borderRadius: 4,
    fontSize: profundidade === 0 ? "1rem" : "0.95rem",
    padding: profundidade === 0 ? "0.75rem 1rem" : "0.6rem 1rem",
    marginLeft: profundidade === 0 ? 0 : "1rem",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    userSelect: "none",
    transition: "background-color 0.15s",
  };

  const wrapperStyle = {
    borderLeft: `4px solid ${paleta.border}`,
    borderRadius: 4,
    backgroundColor: paleta.bg,
    marginBottom: profundidade === 0 ? 0 : 8,
    marginLeft: profundidade === 0 ? 0 : "1rem",
  };

  const handleExpandAll = () => {
    const keys = coletarChaves([node]);
    toggleNode("__expand_batch__", keys);
  };

  const handleCollapseAll = () => {
    const keys = coletarChaves([node]);
    toggleNode("__collapse_batch__", keys);
  };

  return (
    <div style={wrapperStyle} className={isRoot ? "tree-card" : ""}>
      <div
        style={headerStyle}
        onClick={() => toggleNode(node.key)}
        role="button"
        aria-expanded={isOpen}
      >
        {/* Botão expand/collapse all (só no root) */}
        {isRoot && (
          <Button
            variant="outline-light"
            size="sm"
            className="me-2 p-1 px-2"
            style={{ borderColor: "rgba(0,0,0,0.2)", color: paleta.cor }}
            onClick={(e) => { e.stopPropagation(); isOpen ? handleCollapseAll() : handleExpandAll(); }}
            title={isOpen ? "Colapsar tudo" : "Expandir tudo"}
          >
            {isOpen ? <FaCompressAlt size={14} /> : <FaExpandAlt size={14} />}
          </Button>
        )}

        <span className="tree-toggle" style={{ width: 24, textAlign: "center", marginRight: 8, color: paleta.cor, fontSize: "0.75rem" }}>
          <i className={`fas fa-chevron-${isOpen ? "down" : "right"}`} />
        </span>
        <i className={`fas ${paleta.icone} me-2`} />
        <strong style={{ flex: 1 }}>{node.label}</strong>
        <Badge bg="primary" className="ms-2">{node.total} doc(s)</Badge>
      </div>

      <Collapse in={isOpen}>
        <div style={{ padding: isRoot ? "0.5rem 0 1rem 0" : "0.25rem 0" }}>
          {isOpen && (
          <>
          {isLeaf ? (
            /* Tabela de documentos */
            <div style={{ marginLeft: "1.5rem", paddingTop: "0.75rem", paddingBottom: "1rem", overflowX: "auto" }}>
              {/* Botão adicionar */}
              <div style={{ marginBottom: 6 }}>
                <Button
                  variant="outline-success"
                  size="sm"
                  className="d-flex align-items-center"
                  onClick={() => handleAddNew()}
                >
                  <FaPlus size={12} className="me-1" />
                  Adicionar documento
                </Button>
              </div>
              <div className="doc-grid-wrapper">
                <Table size="sm" className="doc-grid-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Fornecedor</th>
                      <th>Tipo Documento</th>
                      <th>Número/Referência</th>
                      <th>Vencimento</th>
                      <th>Vence em</th>
                      <th>Situação</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {node.docs.map((doc) => {
                      const dias = calcDiasParaVcto(doc.dt_vencimento);
                      const situacaoLabel = doc.situacao === "V" ? "Vigente" : doc.situacao === "A" ? "Arquivado" : doc.situacao === "I" ? "Inativo" : doc.situacao || "—";
                      return (
                        <tr key={doc.id} className="tree-doc-row" onClick={() => handleEdit(doc)} role="button">
                          <td>{doc.id}</td>
                          <td>{doc.fornecedor || "—"}</td>
                          <td>{doc.tipo_documento || "—"}</td>
                          <td>{doc.nr_contrato || "—"}</td>
                          <td>{formatarData(doc.dt_vencimento)}</td>
                          <td>
                            <Badge bg={getDiasBadgeVariant(dias)}>
                              {dias !== null ? `${dias} dias` : "—"}
                            </Badge>
                          </td>
                          <td>{situacaoLabel}</td>
                          <td className="text-center" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="p-1"
                              onClick={() => handlePrintPdf(doc)}
                              title="Pré-visualizar PDF"
                            >
                              <FaFilePdf size={16} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>
          ) : (
            /* Níveis filhos */
            <div style={{ paddingTop: 4 }}>
              {node.children.map((child) => (
                <TreeNode
                  key={child.key}
                  node={child}
                  totalNiveis={totalNiveis}
                  expandedNodes={expandedNodes}
                  toggleNode={toggleNode}
                  handleEdit={handleEdit}
                  handleAddNew={handleAddNew}
                  handlePrintPdf={handlePrintPdf}
                  getDiasBadgeVariant={getDiasBadgeVariant}
                />
              ))}
            </div>
          )}
          </>
          )}
        </div>
      </Collapse>
    </div>
  );
}

function PainelConsolidadoDocumentos() {
  const { toast } = useFeedback();
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedDocumento, setSelectedDocumento] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [dateFilters, setDateFilters] = useState({});
  const [filterModalColumn, setFilterModalColumn] = useState(null);
  const [filterModalSelections, setFilterModalSelections] = useState({});
  const [savingFilter, setSavingFilter] = useState(false);
  const [loadingCarregar, setLoadingCarregar] = useState(false);
  const [diasParaVencimento, setDiasParaVencimento] = useState("");
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewHtml, setPdfPreviewHtml] = useState(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const pdfIframeRef = React.useRef(null);

  const ROTA_FILTRO = "PainelConsolidadoDocumentos";
  const token = localStorage.getItem("authToken");
  const { globalData } = useContext(GlobalContext);
  const entidadeID = globalData?.entidade_id || localStorage.getItem("entidade_id");
  const userId = globalData?.user_id || localStorage.getItem("user_id");

  const [niveisAgrupamento, setNiveisAgrupamento] = useState(AGRUPAMENTO_PADRAO);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const fetchDocumentos = useCallback(() => {
    if (!entidadeID || !token) {
      setDocumentos([]);
      setError(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_CARREGAMENTO_MS);
    setLoading(true);
    setError(null);
    api
      .get(`${BASE_URL}/api/documentos/ListarDocumentos/${entidadeID}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          user_id: userId,
          ordenar: "dt_vencimento",
          direcao: "ASC",
          situacao: "V",
        },
        signal: controller.signal,
      })
      .then((response) => {
        setDocumentos(Array.isArray(response.data?.data) ? response.data.data : []);
      })
      .catch((err) => {
        const abortado = err?.name === "CanceledError" || err?.code === "ERR_CANCELED";
        console.error("Erro ao buscar documentos:", err);
        setError(
          abortado
            ? "A consulta de documentos está demorando mais que o esperado. Tente novamente."
            : err.response?.data?.message || "Erro ao carregar documentos."
        );
        setDocumentos([]);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setLoading(false);
      });
  }, [entidadeID, token, userId]);

  useEffect(() => { fetchDocumentos(); }, [fetchDocumentos]);

  // Carrega configuração de agrupamento do banco
  useEffect(() => {
    if (!token) { setLoadingConfig(false); return; }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_CARREGAMENTO_MS);
    api
      .get(`${BASE_URL}/api/configuracoesDashboard/Buscar/${TIPO_DASHBOARD}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      })
      .then((r) => {
        const cfg = r.data?.configuracao;
        if (cfg && Array.isArray(cfg.niveis) && cfg.niveis.length > 0) {
          setNiveisAgrupamento(cfg.niveis);
        }
      })
      .catch(() => {})
      .finally(() => {
        clearTimeout(timeoutId);
        setLoadingConfig(false);
      });
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [token]);

  useEffect(() => {
    if (!userId || !token) return;
    api
      .get(`${BASE_URL}/api/usuarios/ObterFiltrosPadrao/${userId}`, {
        params: { id_entidade: entidadeID || undefined },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        const fj = r.data?.filtros_padrao?.[ROTA_FILTRO];
        if (fj) {
          if (fj.filters)       setFilters(fj.filters);
          if (fj.dateFilters)   setDateFilters(fj.dateFilters);
          if (fj.diasParaVencimento != null) setDiasParaVencimento(String(fj.diasParaVencimento));
        }
      })
      .catch(() => {});
  }, [userId, entidadeID, token]);

  const handleSalvarConfig = (novoNiveis) => {
    setNiveisAgrupamento(novoNiveis);
    setExpandedNodes({});
    setShowConfigModal(false);
    api
      .post(
        `${BASE_URL}/api/configuracoesDashboard/Salvar/${TIPO_DASHBOARD}`,
        { configuracao: { niveis: novoNiveis } },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .catch(() => {});
  };

  const handleCarregarFiltroPadrao = () => {
    if (!userId || !token) return;
    setLoadingCarregar(true);
    api
      .get(`${BASE_URL}/api/usuarios/ObterFiltrosPadrao/${userId}`, {
        params: { id_entidade: entidadeID || undefined },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        const fj = r.data?.filtros_padrao?.[ROTA_FILTRO];
        if (fj) {
          setFilters(fj.filters || {});
          setDateFilters(fj.dateFilters || {});
          setDiasParaVencimento(fj.diasParaVencimento != null ? String(fj.diasParaVencimento) : "");
        } else {
          setFilters({}); setDateFilters({}); setDiasParaVencimento("");
        }
      })
      .catch(() => {})
      .finally(() => setLoadingCarregar(false));
  };

  const handleSalvarFiltro = () => {
    if (!userId || !token) return;
    setSavingFilter(true);
    api
      .get(`${BASE_URL}/api/usuarios/ObterFiltrosPadrao/${userId}`, {
        params: { id_entidade: entidadeID || undefined },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => {
        const fp = r.data?.filtros_padrao || {};
        return api.put(
          `${BASE_URL}/api/usuarios/DefinirFiltrosPadrao/${userId}`,
          { filtros_padrao: { ...fp, [ROTA_FILTRO]: { filters, dateFilters, diasParaVencimento: diasParaVencimento || null } }, id_entidade: entidadeID || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      })
      .then(() => toast.success("Filtro salvo com sucesso!"))
      .catch((err) => toast.error(err.response?.data?.message || "Erro ao salvar filtro."))
      .finally(() => setSavingFilter(false));
  };

  const handleFilter = (value, column) =>
    setFilters((prev) => ({ ...prev, [column]: value.toLowerCase() }));

  const handleDateFilter = (column, operator, value) =>
    setDateFilters((prev) => ({ ...prev, [column]: { operator: operator || "=", value: value ?? "" } }));

  const getUniqueValuesForColumn = useCallback((column) => {
    const getValue = column.getFilterValue || ((row) => row[column.accessor]);
    const values = new Set();
    documentos.forEach((row) => {
      const val = getValue(row);
      if (val != null && val !== "") values.add(String(val).trim());
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [documentos]);

  const openFilterModal = (column) => {
    setFilterModalColumn(column);
    const current = filters[column.accessor];
    setFilterModalSelections(Array.isArray(current) ? current.reduce((acc, v) => ({ ...acc, [v]: true }), {}) : {});
  };

  const handleFilterModalToggle = (value) => {
    const key = String(value).toLowerCase();
    setFilterModalSelections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFilterModalApply = () => {
    if (!filterModalColumn) return;
    const selected = Object.entries(filterModalSelections).filter(([, c]) => c).map(([v]) => v);
    setFilters((prev) => ({ ...prev, [filterModalColumn.accessor]: selected.length > 0 ? selected : "" }));
    setFilterModalColumn(null);
  };

  const hasActiveFilters = () =>
    Object.values(filters).some((v) => v) ||
    Object.values(dateFilters).some((f) => f?.value) ||
    (diasParaVencimento !== "" && diasParaVencimento != null);

  const getActiveFiltersCount = () =>
    Object.keys(filters).filter((k) => filters[k]).length +
    Object.keys(dateFilters).filter((k) => dateFilters[k]?.value).length +
    (diasParaVencimento !== "" ? 1 : 0);

  const clearAllFilters = () => { setFilters({}); setDateFilters({}); setDiasParaVencimento(""); };

  const diasNum = diasParaVencimento !== "" ? parseInt(String(diasParaVencimento).trim(), 10) : null;
  const documentosFiltrados = useMemo(() => documentos.filter((doc) => {
    const normalPass = Object.keys(filters).every((col) => {
      const f = filters[col];
      if (!f) return true;
      const colConfig = FILTER_COLUMNS.find((c) => c.accessor === col);
      const rowVal = getFilterableValue(doc, col, colConfig);
      if (Array.isArray(f)) return f.length > 0 && f.some((v) => rowVal === v);
      if (col === "situacao") return String(rowVal).toLowerCase() === String(f).toLowerCase();
      return rowVal.includes(f);
    });
    const datePass = Object.keys(dateFilters).every((col) => applyDateFilter(doc[col], dateFilters[col]));
    const diasPass = diasNum == null || Number.isNaN(diasNum) ? true : (() => {
      const dias = calcDiasParaVcto(doc.dt_vencimento);
      return dias !== null && dias <= diasNum;
    })();
    return normalPass && datePass && diasPass;
  }), [documentos, filters, dateFilters, diasNum]);

  const tree = useMemo(
    () => buildTreeDynamic(documentosFiltrados, niveisAgrupamento),
    [documentosFiltrados, niveisAgrupamento]
  );

  // Toggle único que suporta expand/collapse em lote
  const toggleNode = (key, batchKeys) => {
    if (key === "__expand_batch__" && batchKeys) {
      setExpandedNodes((prev) => ({ ...prev, ...batchKeys }));
      return;
    }
    if (key === "__collapse_batch__" && batchKeys) {
      setExpandedNodes((prev) => {
        const next = { ...prev };
        Object.keys(batchKeys).forEach((k) => { next[k] = false; });
        return next;
      });
      return;
    }
    setExpandedNodes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleEdit = (doc) => { setSelectedDocumento(doc); setShowModal(true); };
  const handleAddNew = async () => {
    const pode = await podeAdicionarDocumentoAtivo({
      baseUrl: BASE_URL,
      token,
      entidadeID,
      toast,
    });
    if (!pode) return;
    setSelectedDocumento(null);
    setShowModal(true);
  };
  const handleCloseModal = () => { setShowModal(false); setSelectedDocumento(null); fetchDocumentos(); };

  const handlePrintPdf = async (doc) => {
    setPdfPreviewLoading(true);
    setPdfPreviewHtml(null);
    try {
      const html = await gerarDocumentoPdf(doc, {
        nomeEntidade: globalData?.nome_entidade ?? null,
        logotipo: globalData?.logotipo ?? null,
        usuario: globalData?.user ?? null,
      });
      if (!html) { toast.info("Sem dados para exibir!"); return; }
      setPdfPreviewHtml(html);
      setShowPdfPreview(true);
    } finally {
      setPdfPreviewLoading(false);
    }
  };

  const getDiasBadgeVariant = (dias) => {
    if (dias === null) return "secondary";
    if (dias < 0) return "danger";
    if (dias <= 30) return "warning";
    return "success";
  };


  if (loading || loadingConfig) return (
    <div>
      <PageHeader titulo="Painel Consolidado de Documentos" path="/PainelConsolidadoDocumentos" />
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Carregando documentos...</span>
      </div>
    </div>
  );

  if (error) return (
    <div>
      <PageHeader titulo="Painel Consolidado de Documentos" path="/PainelConsolidadoDocumentos" />
      <div className="alert alert-danger">{error}</div>
    </div>
  );

  return (
    <div className="painel-consolidado">
      <PageHeader titulo="Painel Consolidado de Documentos" path="/PainelConsolidadoDocumentos" />

      {/* Barra de ferramentas */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button
            variant={showFilters ? "primary" : "outline-secondary"}
            size="sm"
            className="d-flex align-items-center"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter className="me-1" />
            Filtros
            {hasActiveFilters() && (
              <Badge bg="danger" pill className="ms-1">{getActiveFiltersCount()}</Badge>
            )}
          </Button>
          {hasActiveFilters() && (
            <Button variant="outline-danger" size="sm" className="d-flex align-items-center" onClick={clearAllFilters}>
              <FaTimes className="me-1" />Limpar
            </Button>
          )}
        </div>

        {/* Botão Configurações */}
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center"
            onClick={() => setShowConfigModal(true)}
            title="Configurar agrupamento"
          >
            <FaCog className="me-1" />
            Configurações
          </Button>
        </div>
      </div>

      {/* Painel de Filtros */}
      {showFilters && (
        <Card className="mb-3 bg-light border">
          <Card.Body className="py-2">
            <Row className="g-2">
              {FILTER_COLUMNS.map((column) => {
                if (column.type === "date") {
                  return (
                    <Col md={3} key={column.accessor}>
                      <Form.Group>
                        <Form.Label className="small fw-bold mb-1">{column.Header}</Form.Label>
                        <div className="d-flex gap-1">
                          <Form.Select
                            size="sm"
                            style={{ width: "65px" }}
                            value={dateFilters[column.accessor]?.operator ?? "="}
                            onChange={(e) => handleDateFilter(column.accessor, e.target.value || "=", dateFilters[column.accessor]?.value ?? "")}
                          >
                            <option value="=">=</option>
                            <option value=">">&gt;</option>
                            <option value="<">&lt;</option>
                            <option value=">=">&gt;=</option>
                            <option value="<=">&lt;=</option>
                          </Form.Select>
                          <Form.Control
                            type="date"
                            size="sm"
                            className="flex-grow-1"
                            onChange={(e) => handleDateFilter(column.accessor, dateFilters[column.accessor]?.operator ?? "=", e.target.value)}
                            value={dateFilters[column.accessor]?.value ?? ""}
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  );
                }
                if (column.Header === "Situação") {
                  return (
                    <React.Fragment key={column.accessor}>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small fw-bold mb-1">{column.Header}</Form.Label>
                          <Form.Select size="sm" onChange={(e) => handleFilter(e.target.value, column.accessor)} value={filters[column.accessor] || ""}>
                            <option value="">Todos</option>
                            <option value="V">Vigente</option>
                            <option value="A">Arquivado</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="small fw-bold mb-1">Nº de dias (vencer em até)</Form.Label>
                          <Form.Control
                            type="number" size="sm" min={0} placeholder="Em branco = todos"
                            value={diasParaVencimento}
                            onChange={(e) => setDiasParaVencimento(e.target.value)}
                          />
                          <Form.Text className="text-muted small">Ex.: 30 = vence em até 30 dias</Form.Text>
                        </Form.Group>
                      </Col>
                    </React.Fragment>
                  );
                }
                const filterVal = filters[column.accessor];
                const isArrayFilter = Array.isArray(filterVal) && filterVal.length > 0;
                return (
                  <Col md={3} key={column.accessor}>
                    <Form.Group>
                      <Form.Label className="small fw-bold mb-1">
                        {column.Header}
                        {isArrayFilter && <Badge bg="info" className="ms-1" pill>{filterVal.length}</Badge>}
                      </Form.Label>
                      <InputGroup size="sm">
                        <InputGroup.Text className="bg-white" style={{ cursor: "pointer" }} onClick={() => openFilterModal(column)} role="button" title="Abrir lista de valores">
                          <FaSearch size={12} color="#6c757d" />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder={isArrayFilter ? `${filterVal.length} selecionado(s)` : "Pesquisar..."}
                          onChange={(e) => handleFilter(e.target.value, column.accessor)}
                          value={Array.isArray(filterVal) ? "" : (filterVal || "")}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                );
              })}
            </Row>
            <div className="mt-2 pt-2 border-top">
              <Button variant="outline-primary" size="sm" onClick={handleSalvarFiltro} disabled={savingFilter}>
                <FaSave className="me-1" />Salvar Filtro
              </Button>
              <span className="text-muted small ms-2">Salva os filtros atuais como padrão para esta tela</span>
              <div className="mt-2">
                <Button variant="outline-secondary" size="sm" onClick={handleCarregarFiltroPadrao} disabled={loadingCarregar}>
                  <FaDownload className="me-1" />Carregar Filtro Padrão
                </Button>
                <span className="text-muted small ms-2">Aplica o filtro padrão salvo</span>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Árvore dinâmica */}
      <div className="tree-cubo">
        {tree.length === 0 ? (
          <Card className="tree-card">
            <Card.Body>
              <p className="text-muted mb-0">Nenhum documento encontrado.</p>
            </Card.Body>
          </Card>
        ) : (
          tree.map((node) => (
            <TreeNode
              key={node.key}
              node={node}
              totalNiveis={niveisAgrupamento.length}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
              handleEdit={handleEdit}
              handleAddNew={handleAddNew}
              handlePrintPdf={handlePrintPdf}
              getDiasBadgeVariant={getDiasBadgeVariant}
            />
          ))
        )}
      </div>

      {showModal && (
        <DocumentosModal
          show={showModal}
          handleClose={handleCloseModal}
          documento={selectedDocumento}
          fetchDocumentos={fetchDocumentos}
        />
      )}

      {/* Modal Configurar Agrupamento */}
      <PainelConsolidadoConfigModal
        show={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        niveis={niveisAgrupamento}
        onSalvar={handleSalvarConfig}
      />

      {/* Modal filtro por valor */}
      <Modal show={!!filterModalColumn} onHide={() => setFilterModalColumn(null)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Filtrar por {filterModalColumn?.Header || ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {filterModalColumn && (
            <>
              <div className="p-2 border-bottom d-flex gap-1">
                <Button variant="outline-primary" size="sm" onClick={() => { const next = {}; getUniqueValuesForColumn(filterModalColumn).forEach(v => { next[String(v).toLowerCase()] = true; }); setFilterModalSelections(next); }}>Todos</Button>
                <Button variant="outline-secondary" size="sm" onClick={() => setFilterModalSelections({})}>Limpar</Button>
              </div>
              <ListGroup variant="flush">
                {getUniqueValuesForColumn(filterModalColumn).map((val) => {
                  const key = String(val).toLowerCase();
                  return (
                    <ListGroup.Item key={key} action active={!!filterModalSelections[key]} onClick={() => handleFilterModalToggle(val)} style={{ cursor: "pointer" }}>
                      {val || <em className="text-muted">(vazio)</em>}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer-padrao">
          <div className="modal-footer-acoes-esquerda" />
          <div className="modal-footer-acoes-direita">
            <Button variant="light" className="modal-footer-btn-cancelar" onClick={() => setFilterModalColumn(null)}>Cancelar</Button>
            <Button variant="success" className="modal-footer-btn-salvar" onClick={handleFilterModalApply}>Aplicar</Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Modal PDF */}
      <Modal show={showPdfPreview} onHide={() => setShowPdfPreview(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Pré-visualização</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ minHeight: "70vh" }}>
          {pdfPreviewLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5"><Spinner animation="border" /></div>
          ) : pdfPreviewHtml ? (
            <iframe ref={pdfIframeRef} title="Pré-visualização PDF" srcDoc={pdfPreviewHtml} style={{ width: "100%", height: "70vh", border: "none" }} />
          ) : (
            <div className="text-center text-muted py-5">Nenhum conteúdo para exibir.</div>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer-padrao">
          <div className="modal-footer-acoes-esquerda" />
          <div className="modal-footer-acoes-direita">
            <Button variant="success" className="modal-footer-btn-salvar" onClick={() => pdfIframeRef.current?.contentWindow?.print()} disabled={!pdfPreviewHtml}>Imprimir</Button>
            <Button variant="light" className="modal-footer-btn-cancelar" onClick={() => setShowPdfPreview(false)}>Fechar</Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default PainelConsolidadoDocumentos;
